import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IonIcon, IonSpinner, IonButton } from '@ionic/react';
import { 
  closeCircleOutline,
  alertCircleOutline,
  documentTextOutline,
  gitBranchOutline,
  rocketOutline,
  refreshOutline,
  stopCircleOutline
} from 'ionicons/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useCreatePR } from '../../hooks/useCreatePR';
import ExecutionPipeline from '../../components/shared/ExecutionPipeline';
import { CodeDiffViewer } from '../../components/shared/CodeDiffViewer';
import TestCasesViewer from '../../components/shared/TestCasesViewer/TestCasesViewer';
import { AnalysisDiff } from '../../components/shared/AnalysisDiff';
import TicketTitleRow from '../../components/shared/TicketTitleRow/TicketTitleRow';
import { splitCodeImplByRepo } from '../../utils/diffUtils';
import { extractSuggestedChanges } from '../../utils/ticketUtils';
import './AnalysisDetailsPage.css';

const isSubmoduleOnlyDiff = (diff) => {
  if (!diff) return true;
  const lines = diff.split('\n').filter(l => l.trim());
  const hasSubproject = lines.some(l => l.includes('Subproject commit'));
  const hasRealChanges = lines.some(l => 
    (l.startsWith('+') || l.startsWith('-')) && 
    !l.startsWith('+++') && 
    !l.startsWith('---') &&
    !l.includes('Subproject commit')
  );
  return hasSubproject && !hasRealChanges;
};

const AnalysisDetailsPage = ({ setExecutionName, setExecutionStatus }) => {
  const { executionId, projectId } = useParams();
  const navigate = useNavigate();
  const [apiToken, setApiToken] = useState(null);
  const [projectSettings, setProjectSettings] = useState({});
  const apiTokenFetched = useRef(false);
  const [execution, setExecution] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollIntervalRef = useRef(null);
  const saveTimerRef = useRef(null);

  const handleReanalyze = async () => {
    if (!execution?.projectId || !execution?.ticketKey) return;
    setReanalyzing(true);
    try {
      const response = await api.post(
        `/projects/${execution.projectId}/tickets/${execution.ticketKey}/analyze`,
        {}
      );
      if (response.executionId) {
        navigate(`/projects/${execution.projectId}/analysis/${response.executionId}`);
      }
    } catch (error) {
      console.error('Error starting re-analysis:', error);
      setReanalyzing(false);
    }
  };

  const handleCancel = async () => {
    if (!executionId) return;
    if (!window.confirm('Are you sure you want to cancel this execution? This cannot be undone.')) return;
    setCancelling(true);
    try {
      await api.post(`/executions/${executionId}/cancel`);
      fetchAnalysisDetails();
    } catch (error) {
      console.error('Error cancelling execution:', error);
      alert(error.response?.data?.message || 'Failed to cancel execution');
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    fetchAnalysisDetails();

    // Poll every 1.5 seconds (fast enough to catch short-lived steps like setup)
    pollIntervalRef.current = setInterval(() => {
      fetchAnalysisDetails();
    }, 1500);

    // Cleanup interval on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  // Stop polling when execution reaches terminal state or all steps are done
  useEffect(() => {
    const terminalStatuses = ['completed', 'failed', 'cancelled', 'blocked', 'insufficient_context'];
    const allStepsDone = execution?.steps?.length > 0 && execution.steps.every(s => s.status === 'success');
    if (terminalStatuses.includes(execution?.status) || allStepsDone) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [execution?.status, execution?.steps]);

  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  const handleTestCasesChange = useCallback((updatedTestCases) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await api.patch(`/executions/${executionId}/test-cases`, { testCases: updatedTestCases });
      } catch (err) {
        console.error('Failed to save test cases:', err);
      }
    }, 800);
  }, [executionId]);

  const fetchAnalysisDetails = async () => {
    try {
      const data = await api.get(`/executions/${executionId}`);
      setExecution(data);
      
      if (setExecutionName) {
        setExecutionName(data.name || (data.ticketKey && data.ticketSummary ? `${data.ticketKey}: ${data.ticketSummary}` : data.ticketKey) || 'Analysis');
      }
      if (setExecutionStatus) {
        setExecutionStatus(data.status);
      }
      if (data.ticketKey && projectId && !ticket) {
        api.get(`/projects/${projectId}/tickets/${data.ticketKey}`).then(t => {
          setTicket(t?.ticket || t);
        }).catch(() => {});
      }
      if (!apiTokenFetched.current && projectId) {
        apiTokenFetched.current = true;
        api.get(`/projects/${projectId}`).then(d => {
          const proj = d?.project || {};
          setApiToken(proj.apiToken || null);
          setProjectSettings({ aiAgent: proj.aiAgent, aiModel: proj.aiModel });
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const { isCreatingRepo, handleCreatePR } = useCreatePR({
    execution,
    fallbackTitle: 'Code changes from analysis'
  });

  const getAnalysisSteps = () => {
    const reportedSteps = execution?.steps || [];
    if (reportedSteps.length === 0) return [];

    const steps = reportedSteps.map(s => ({
      ...s,
      displayName: s.displayName || s.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      status: s.status || 'pending',
    }));

    if ((execution?.status === 'starting' || execution?.status === 'running' || execution?.status === 'queued') &&
        steps.every(s => s.status === 'pending')) {
      steps[0].status = 'running';
    }

    if (execution?.status === 'failed') {
      let failedFound = false;
      steps.forEach(s => {
        if (s.status === 'running' || s.status === 'in_progress') {
          s.status = 'failed';
          failedFound = true;
        } else if (s.status === 'queued' || s.status === 'pending') {
          s.status = 'cancelled';
        }
      });
      if (!failedFound) {
        const firstNonComplete = steps.find(s => s.status !== 'completed' && s.status !== 'success');
        if (firstNonComplete) firstNonComplete.status = 'failed';
      }
    }

    if (execution?.status === 'blocked' || execution?.status === 'insufficient_context') {
      steps.forEach(s => {
        if (s.status === 'queued' || s.status === 'pending') {
          s.status = 'skipped';
        }
      });
    }

    return steps;
  };
  
  const steps = getAnalysisSteps();
  
  // Parse artifacts from execution
  const artifacts = execution?.artifacts || {};
  const codeImpl = artifacts.codeImplementation;
  const tests = artifacts.tests;
  const repoCodeSections = useMemo(
    () => splitCodeImplByRepo(codeImpl, execution?.repos),
    [codeImpl, execution?.repos]
  );
  const suggestedChanges = useMemo(
    () => extractSuggestedChanges(execution, ticket),
    [execution, ticket]
  );

  if (loading) {
    return (
      <div className="analysis-page">
        <div className="analysis-loading">
          <IonSpinner />
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="analysis-page">
        <p>Analysis not found</p>
      </div>
    );
  }

  const isActuallyComplete = execution?.completedAt || 
    (execution?.steps?.length > 0 && execution.steps.every(s => s.status === 'success'));

  let titleActions = null;
  if (execution.status === 'failed' && !isActuallyComplete) {
    titleActions = (
      <IonButton size="small" color="primary" onClick={handleReanalyze} disabled={reanalyzing}>
        {reanalyzing ? <IonSpinner name="crescent" /> : <><IonIcon icon={refreshOutline} slot="start" />Re-analyze</>}
      </IonButton>
    );
  } else if (['running', 'queued', 'starting'].includes(execution.status) && !isActuallyComplete) {
    titleActions = (
      <IonButton size="small" color="danger" onClick={handleCancel} disabled={cancelling}>
        {cancelling ? <IonSpinner name="crescent" /> : <><IonIcon icon={stopCircleOutline} slot="start" />Cancel</>}
      </IonButton>
    );
  }

  return (
    <div className="analysis-page breadcrumb-spacing">
      {/* Analysis run title */}
      <TicketTitleRow
        title={execution.ticketKey && execution.ticketSummary
          ? `${execution.ticketKey}: ${execution.ticketSummary}`
          : execution.name || execution.ticketKey || 'Analysis'}
        icon={rocketOutline}
        actions={titleActions}
      />

      {/* Error Display at Top (if execution failed) */}
      {execution.status === 'failed' && execution.error && (
        <div className="analysis-error">
          <IonIcon icon={closeCircleOutline} />
          <div>
            <h4>Analysis Failed</h4>
            <p>{execution.error}</p>
          </div>
        </div>
      )}

      {/* Cancelled Status Display */}
      {execution.status === 'cancelled' && (
        <div className="analysis-cancelled">
          <IonIcon icon={closeCircleOutline} />
          <div>
            <h4>Analysis Cancelled</h4>
            <p>{execution.error || 'Execution was cancelled by user'}</p>
          </div>
        </div>
      )}

      {/* Validation Blocked Display - when ticket needs more info */}
      {(execution.status === 'blocked' || execution.status === 'insufficient_context') && (
        <div className="analysis-blocked">
          <div className="analysis-blocked-content">          
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon icon={alertCircleOutline} />
              Additional Information Needed
            </h4>
            {execution.artifacts?.analysis?.structured?.validation?.reasoning && (
              <p>{execution.artifacts.analysis.structured.validation.reasoning}</p>
            )}
            {execution.artifacts?.analysis?.structured?.validation?.blockers && (
              <div style={{ marginTop: '12px' }}>
                <strong>Items to Resolve:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {execution.artifacts.analysis.structured.validation.blockers.map((blocker, i) => (
                    <li key={i}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ marginTop: '12px', fontSize: '14px', opacity: 0.8 }}>
              Please update the ticket with the required information and run the analysis again.
            </p>
          </div>
        </div>
      )}

      {/* Analysis Report Preview */}
      {execution.reportUrl && (
        <div className="analysis-report-preview">
          <IonIcon icon={documentTextOutline} />
          <a href={execution.reportUrl} target="_blank" rel="noopener noreferrer">
            View Full Analysis Report
          </a>
        </div>
      )}

      {/* Execution Pipeline - ALWAYS SHOW */}
      <ExecutionPipeline steps={steps} executionStatus={execution?.status} />

      {/* AI Suggested Changes */}
      {suggestedChanges.length > 0 && (
        <AnalysisDiff
          suggestedChanges={suggestedChanges}
          analysisId={executionId}
          projectId={projectId}
          ticketKey={execution.ticketKey}
          onApplyComplete={() => fetchAnalysisDetails()}
          defaultCollapsed
        />
      )}

      {/* Code Implementation Preview - Per Repo */}
      {repoCodeSections.length > 0 && (
        <div className={`analysis-artifacts ${repoCodeSections.length > 1 ? 'multi-repo-group' : ''}`}>
          {repoCodeSections.map((section) => {
            const sectionDiff = section.diff;
            if (!sectionDiff || isSubmoduleOnlyDiff(sectionDiff)) return null;
            const repoLabel = section.repoName
              ? `Code Changes — ${section.repoName}`
              : 'Code Changes';
            return (
              <div className="artifact-section" key={section.repoName || 'default'}>
                <div className="code-diff">
                  <CodeDiffViewer
                    diff={sectionDiff}
                    title={repoLabel}
                    fileContents={section.fileContents}
                    changedFiles={section.changedFiles}
                    showFileList={true}
                    showFileTree={true}
                    defaultView="side-by-side"
                    headerActions={
                      <button 
                        className="create-pr-btn"
                        onClick={() => handleCreatePR(section.repoName)}
                        disabled={isCreatingRepo(section.repoName)}
                        title={`Create a pull request for ${section.repoName || 'these changes'}`}
                      >
                        {isCreatingRepo(section.repoName) ? (
                          <>
                            <IonSpinner name="crescent" />
                            Create Pull Request
                          </>
                        ) : (
                          <>
                            <IonIcon icon={gitBranchOutline} />
                            Create Pull Request
                          </>
                        )}
                      </button>
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Test Cases - Editable */}
      {tests?.structured?.testCases && tests.structured.testCases.length > 0 && (
        <TestCasesViewer 
          testCases={tests.structured.testCases}
          summary={tests.structured.summary}
          onChange={handleTestCasesChange}
          executionId={executionId}
          projectId={apiToken}
          aiAgent={projectSettings.aiAgent}
          aiModel={projectSettings.aiModel}
        />
      )}

    </div>
  );
};

export default AnalysisDetailsPage;
